<template>
  <div :class="workCentersData ? 'w-auto flex' : 'w-1/4 flex'">
    <el-select
      clearable
      filterable
      v-model="_searchItem.value"
      class="w-full"
      size="medium"
      :placeholder="$t('selectOne')"
    >
      <el-option v-for="(item, key) in filteredSuppliersData" :key="key" :label="item.name" :value="key">
        {{ item.name }}
      </el-option>
    </el-select>

    <el-select
      clearable
      filterable
      v-model="_searchItem.warehouseId"
      class="w-full"
      size="medium"
      :key="filteredWarehousesData"
      :placeholder="$t('selectOne')"
      @change="onWarehouseValueChange"
    >
      <el-option v-for="(item, key) in filteredWarehousesData" :key="key" :label="item.name" :value="key">
        {{ item.name }}
      </el-option>
    </el-select>

    <el-select
      clearable
      filterable
      v-model="_searchItem.workCenterId"
      class="w-full"
      size="medium"
      :key="filteredWorkCentersData"
      :placeholder="$t('selectOne')"
      @change="onWorkCenterValueChange"
    >
      <el-option v-for="(item, key) in filteredWorkCentersData" :key="key" :label="item.name" :value="key">
        {{ item.name }}
      </el-option>
    </el-select>
  </div>
</template>

<script lang="ts">
import { ISupplier, IWarehouse, IWorkcenter } from '@/apis/masterDataAPI'
import SearchItemField from '@/components/barcodeSearch/SearchItemField.vue'
import cloneDeep from 'lodash/cloneDeep'
import { Options } from 'vue-class-component'
import { Mixins, Prop } from 'vue-property-decorator'

@Options({ components: {}, name: 'SearchItemSupplier' })
export default class SearchItemSupplier extends Mixins(SearchItemField) {
  @Prop({ type: Object }) suppliersData!: Record<string, ISupplier>
  @Prop({ type: Object }) workCentersData?: Record<string, IWorkcenter> | undefined
  @Prop({ type: Object }) warehousesData?: Record<string, IWarehouse> | undefined

  onWorkCenterValueChange(newVal: string) {
    if (newVal) {
      const selectedWorkCenter = this.workCentersData?.[newVal] as IWorkcenter

      // Auto-select warehouse based on workcenter's wipWarehouse, then supplier via warehouse chain
      if (selectedWorkCenter?.wipWarehouse && this.warehousesData) {
        const warehouseKey = Object.keys(this.warehousesData).find(
          (key) => this.warehousesData![key].code === selectedWorkCenter.wipWarehouse
        )
        if (warehouseKey) {
          this._searchItem.warehouseId = warehouseKey

          // Auto-select supplier via warehouse chain: workcenter → warehouse → supplier
          const linkedWarehouse = this.warehousesData[warehouseKey]
          if (linkedWarehouse?.vendorCode && this.suppliersData) {
            const supplierKey = Object.keys(this.suppliersData).find(
              (key) => this.suppliersData[key].code === linkedWarehouse.vendorCode
            )
            if (supplierKey) {
              this._searchItem.value = supplierKey
            }
          }
        }
      }
    } else {
      this._searchItem.workCenterId = undefined
    }
  }

  onWarehouseValueChange(newVal: string) {
    if (!newVal) {
      this._searchItem.warehouseId = undefined
    }
  }

  get filteredSuppliersData() {
    const selectedWorkCenter = this.workCentersData?.[this._searchItem.workCenterId] as IWorkcenter
    const selectedWarehouse = this.warehousesData?.[this._searchItem.warehouseId] as IWarehouse

    // Resolve vendor code via warehouse chain: warehouse.vendorCode = supplier.code
    // If workcenter selected but no warehouse, find the linked warehouse to get vendorCode
    let vendorCode: string | undefined = selectedWarehouse?.vendorCode
    if (!vendorCode && selectedWorkCenter?.wipWarehouse && this.warehousesData) {
      const linkedWarehouse = Object.values(this.warehousesData).find(
        (w) => w.code === selectedWorkCenter.wipWarehouse
      )
      vendorCode = linkedWarehouse?.vendorCode
    }

    if (vendorCode) {
      const filtered: Record<string, ISupplier> = {}
      Object.entries(this.suppliersData).forEach(([key, supplier]) => {
        if (supplier.code === vendorCode) {
          filtered[key] = supplier
        }
      })
      return filtered
    }

    return this.suppliersData
  }

  get filteredWarehousesData() {
    if (!this.warehousesData) {
      return {}
    }

    const selectedSupplier = this.suppliersData[this._searchItem.value] as ISupplier
    const selectedWorkCenter = this.workCentersData?.[this._searchItem.workCenterId] as IWorkcenter

    // Filter warehouses by supplier's code OR workcenter's wipWarehouse
    if (selectedSupplier?.code || selectedWorkCenter?.wipWarehouse) {
      const filtered: Record<string, IWarehouse> = {}
      Object.entries(this.warehousesData).forEach(([key, warehouse]) => {
        let matchesSupplier = true
        let matchesWorkCenter = true

        if (selectedSupplier?.code) {
          matchesSupplier = warehouse.vendorCode === selectedSupplier.code
        }

        if (selectedWorkCenter?.wipWarehouse) {
          matchesWorkCenter = warehouse.code === selectedWorkCenter.wipWarehouse
        }

        if (matchesSupplier && matchesWorkCenter) {
          filtered[key] = warehouse
        }
      })
      return filtered
    }

    return this.warehousesData
  }

  get filteredWorkCentersData() {
    if (!this.workCentersData) {
      return {}
    }

    const selectedSupplier = this.suppliersData[this._searchItem.value] as ISupplier
    const selectedWarehouse = this.warehousesData?.[this._searchItem.warehouseId] as IWarehouse

    // Filter workcenters by supplier's code AND/OR warehouse's code
    if (selectedSupplier?.code || selectedWarehouse?.code) {
      const filtered: Record<string, IWorkcenter> = {}
      Object.entries(this.workCentersData).forEach(([key, workcenter]) => {
        let matchesSupplier = true
        let matchesWarehouse = true

        if (selectedSupplier?.code && this.warehousesData) {
          // Match via warehouse chain: workcenter.wipWarehouse → warehouse.code → warehouse.vendorCode = supplier.code
          const linkedWarehouse = Object.values(this.warehousesData).find(
            (w) => w.code === workcenter.wipWarehouse
          )
          matchesSupplier = linkedWarehouse?.vendorCode === selectedSupplier.code
        }

        if (selectedWarehouse?.code) {
          matchesWarehouse = workcenter.wipWarehouse === selectedWarehouse.code
        }

        if (matchesSupplier && matchesWarehouse) {
          filtered[key] = workcenter
        }
      })
      return filtered
    }

    return this.workCentersData
  }

  created() {
    this.oldData = cloneDeep(this._searchItem)
  }
}
</script>
